import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { Search, Loader2, MapPin, CheckCircle2, ShieldCheck } from "lucide-react";

const DealerCard = ({ d, selected, onClick }) => (
  <button data-testid={`find-dealer-option-${d.id}`} onClick={onClick}
    className={`w-full text-left p-3 rounded-xl border-2 transition-[border-color,background-color] ${selected ? "border-[#8B0000] bg-[#FDF2F2]" : "border-gray-200 hover:border-gray-300 bg-white"}`}>
    <div className="font-semibold text-gray-900 text-sm">{d.name}</div>
    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" /> {[d.city, d.district, d.state].filter(Boolean).join(", ")} · PIN {d.pin || "-"}</div>
  </button>
);

export default function FindDealer() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const linkId = sp.get("l") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [dealers, setDealers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => {
    api.get(`/agent/dealer-link/${token}`, { params: { l: linkId } }).then((r) => setInfo(r.data)).catch((e) => setError(apiErr(e)));
  }, [token, linkId]);

  useEffect(() => {
    const v = q.trim();
    if (v.length < 2) { setDealers([]); return undefined; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const digits = /^\d+$/.test(v);
        const { data } = await api.get("/dealers/search", { params: { pin: digits ? v : "", city: digits ? "" : v, text: v, limit: 15 } });
        setDealers(data.mode === "all" ? [] : data.dealers || []);
      } catch { setDealers([]); } finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const confirm = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/agent/dealer-link/${token}`, { dealer_id: sel.id, link_id: linkId }); setDone(data.dealer); setTimeout(() => navigate(`/chat/${token}`), 1500); }
    catch (e) { setError(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[100dvh] bg-[#F6F3EE] flex justify-center">
      <div className="w-full max-w-md flex flex-col" data-testid="find-dealer-page">
        <header className="bg-[#8B0000] text-white px-5 pt-6 pb-8 rounded-b-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-white/70">Jewel Paints Rewards</div>
          <h1 className="font-display text-3xl font-bold mt-2">Find My Dealer</h1>
          <p className="text-sm text-white/80 mt-1 flex items-center gap-1" data-testid="find-dealer-identity">
            <ShieldCheck className="h-4 w-4" /> {info ? `Hi ${info.member_name} · ${info.mobile_masked}` : "Secure link"}
          </p>
        </header>
        <main className="flex-1 px-5 -mt-5 pb-28">
          {error && <div data-testid="find-dealer-error" className="bg-white rounded-2xl p-6 text-center text-sm text-gray-700 shadow-sm">{error}</div>}
          {done && (
            <div data-testid="find-dealer-success" className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
              <h2 className="font-display text-xl font-bold text-gray-900 mt-3">Dealer linked!</h2>
              <p className="text-sm text-gray-600 mt-1">{done}</p>
              <button data-testid="find-dealer-back-btn" onClick={() => navigate(`/chat/${token}`)} className="mt-4 text-sm font-bold text-[#8B0000]">Back to chat →</button>
            </div>
          )}
          {info && !done && !error && (
            <>
              <div className="relative bg-white rounded-2xl shadow-sm">
                <Search className="h-5 w-5 absolute left-4 top-4 text-gray-400" />
                <input data-testid="find-dealer-search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="PIN code, area or dealer name" className="w-full pl-12 pr-4 py-4 rounded-2xl outline-none text-base" />
              </div>
              <div className="mt-4 space-y-2" data-testid="find-dealer-results">
                {searching && <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-[#8B0000]" /></div>}
                {!searching && q.trim().length >= 2 && dealers.length === 0 && <p className="text-center text-sm text-gray-500 py-6">No dealers found. Try another PIN, area or name.</p>}
                {!searching && q.trim().length < 2 && <p className="text-center text-sm text-gray-400 py-6">Start typing to search dealers</p>}
                {dealers.map((d) => <DealerCard key={d.id} d={d} selected={sel?.id === d.id} onClick={() => setSel(d)} />)}
              </div>
            </>
          )}
        </main>
        {info && !done && !error && (
          <div className="fixed bottom-0 inset-x-0 flex justify-center">
            <div className="w-full max-w-md p-4 bg-[#F6F3EE]/95 backdrop-blur border-t border-gray-200">
              <button data-testid="find-dealer-confirm-btn" disabled={!sel || busy} onClick={confirm}
                className="w-full bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold py-4 rounded-full disabled:opacity-40 active:scale-[0.98] transition-[background-color,transform] flex items-center justify-center gap-2">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} {sel ? `Confirm ${sel.name.split("-")[0]}` : "Select your dealer"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
